import { FINES } from '@accra/shared';
import type { EventBus } from '../../core/events';
import type { Rng } from '../../core/rng';
import type { Checkpoint, RunState } from '../../game/RunState';
import { nextStation } from '../../world/fuelStations';
import { activeRoute } from '../../world/route';
import type { DrivingAssist } from '../driving';
import { nextJunction } from '../junctions/signals';
import { stopAt } from '../passengers/stopSequence';

export const CHECKPOINT = {
  /** First checkpoint, metres into the shift, then one every so often. */
  firstAt: 800,
  spacing: [1100, 1900] as const,
  /** The officer is set up this far before the bus reaches him. */
  setUpAhead: 260,
  /** "Checkpoint ahead" warning. */
  noticeAt: 170,
  /** You must stop this far before the officer (front of the bus to the officer): at most / at least. */
  stopWithin: 22,
  stopAtLeast: 1.5,
  /** Seconds the check takes: time lost. */
  checkSeconds: 3.2,
  /** Chance of a papers problem even with a clean record. */
  problemChance: 0.12,
  /** Chance an officer asks for a dash, how long he waits for an answer, and the extra papers risk if you say no. */
  dashChance: 0.4,
  askSeconds: 7,
  refusedExtraChance: 0.25,
  /** Gone from the road this far behind the bus. */
  clearBehind: 30,
} as const;

const OFF: DrivingAssist = { maxSpeed: Number.POSITIVE_INFINITY };
const HOLD: DrivingAssist = { maxSpeed: 0 };
const GLIDE = { range: 40, engageBelow: 11, decel: 5, stopGap: 6 } as const;

/** Keeps checkpoints off junctions, bus stops, petrol stations and the U-turn at each end of the route. */
function clearAt(state: RunState, s: number): boolean {
  if (Math.abs(nextJunction(s - 20).position - s) < 90) return false;
  for (let seq = state.stop.seq; seq < state.stop.seq + 8; seq += 1) if (Math.abs(stopAt(seq).position - s) < 80) return false;
  const station = nextStation(s - 60);
  if (station && Math.abs(station.position - s) < 100) return false;
  const into = s % activeRoute().length;
  return into > 120 && into < activeRoute().length - 120;
}

function front(state: RunState): number {
  return state.bus.s + state.bus.size.length / 2;
}

/** While being checked the trotro is held; before that, a gentle glide helps you stop in time (gas overrides). */
export function checkpointAssist(state: RunState, gas: boolean): DrivingAssist {
  const cp = state.checkpoint.current;
  if (!cp) return OFF;
  if (cp.phase === 'checking') return HOLD;
  if (cp.phase !== 'approach' || gas || state.bus.speed > GLIDE.engageBelow) return OFF;
  const gap = cp.s - front(state);
  if (gap < 0 || gap > GLIDE.range) return OFF;
  return { maxSpeed: Math.min(Math.sqrt(2 * GLIDE.decel * Math.max(0, gap - GLIDE.stopGap)), GLIDE.engageBelow) };
}

function spawn(state: RunState, rng: Rng): void {
  const cps = state.checkpoint;
  if (cps.current || state.bus.s < cps.nextAt - CHECKPOINT.setUpAhead) return;
  let at = cps.nextAt;
  while (!clearAt(state, at)) at += 60;
  cps.lastId += 1;
  cps.nextAt = at + rng.range(CHECKPOINT.spacing[0], CHECKPOINT.spacing[1]);
  cps.current = { id: cps.lastId, s: at, phase: 'approach', checkedFor: 0, papersProblem: false, announced: false, asksDash: rng.next() < CHECKPOINT.dashChance, dash: 'none', askedFor: 0 };
}

function ran(state: RunState, cp: Checkpoint, events: EventBus): void {
  cp.phase = 'ran';
  state.stats.checkpointsRun += 1;
  state.stats.finesPesewas += FINES.checkpoint;
  events.emit('checkpoint:ran', { finePesewas: FINES.checkpoint });
}

function finishCheck(state: RunState, cp: Checkpoint, rng: Rng, events: EventBus): void {
  const { stats } = state;
  // Papers are fine if you've driven properly this shift (no red lights, zebras or rear-ends), bar a little bad luck.
  const record = stats.redLightsRun + stats.pedestrianOffences + stats.rearEnds;
  const chance = CHECKPOINT.problemChance + (cp.dash === 'refused' ? CHECKPOINT.refusedExtraChance : 0);
  cp.papersProblem = record > 0 || rng.next() < chance;
  cp.phase = 'cleared';
  if (cp.papersProblem) {
    stats.papersPaid += 1;
    stats.finesPesewas += FINES.papers;
  }
  events.emit('checkpoint:cleared', { papersProblem: cp.papersProblem, finePesewas: cp.papersProblem ? FINES.papers : 0, dashed: false });
}

export type DashChoice = 'ten' | 'twenty' | 'refuse';

/** Your answer when the officer asks for a dash. A dash settles it on the spot: "all good", papers or not. */
export function answerDash(state: RunState, choice: DashChoice, events: EventBus): void {
  const cp = state.checkpoint.current;
  if (!cp || cp.phase !== 'checking' || cp.dash !== 'asking') return;
  if (choice === 'refuse') {
    cp.dash = 'refused';
    events.emit('checkpoint:dashRefused', {});
    return;
  }
  const amount = choice === 'ten' ? FINES.dashTen : FINES.dashTwenty;
  if (choice === 'ten') state.stats.dashTen += 1;
  else state.stats.dashTwenty += 1;
  state.stats.finesPesewas += amount;
  cp.dash = 'paid';
  cp.papersProblem = false;
  cp.phase = 'cleared';
  events.emit('checkpoint:dash', { amountPesewas: amount });
  events.emit('checkpoint:cleared', { papersProblem: false, finePesewas: 0, dashed: true });
}

/** Sets up checkpoints ahead, waves you down, checks you if you stop, and fines you if you drive past. */
export function updateCheckpoint(state: RunState, dt: number, rng: Rng, events: EventBus): void {
  spawn(state, rng);
  const cp = state.checkpoint.current;
  if (!cp) return;
  const gap = cp.s - front(state);
  if (cp.phase === 'approach') {
    if (!cp.announced && gap < CHECKPOINT.noticeAt) {
      cp.announced = true;
      events.emit('checkpoint:ahead', { distance: Math.round(gap) });
    }
    if (gap < -1) ran(state, cp, events);
    else if (state.bus.speed < 1.2 && gap <= CHECKPOINT.stopWithin && gap >= CHECKPOINT.stopAtLeast) cp.phase = 'checking';
  } else if (cp.phase === 'checking') {
    if (cp.asksDash && cp.dash === 'none') {
      cp.dash = 'asking';
      events.emit('checkpoint:dashAsk', {});
    }
    if (cp.dash === 'asking') {
      cp.askedFor += dt;
      if (cp.askedFor >= CHECKPOINT.askSeconds) answerDash(state, 'refuse', events);
    } else {
      cp.checkedFor += dt;
      if (cp.checkedFor >= CHECKPOINT.checkSeconds) finishCheck(state, cp, rng, events);
    }
  }
  if (state.bus.s - cp.s > CHECKPOINT.clearBehind && (cp.phase === 'cleared' || cp.phase === 'ran')) state.checkpoint.current = null;
}
